import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { clean, stagger, stripHtml } from '../utils/format';
import { useIsEditing, useMediaQuery, useRawProperty } from '../utils/hooks';
import { PATHS, pagePath } from '../utils/site';
import { feeFacts, hasFees, petCells, petSummary } from '../utils/fees';
import { DetailsRow } from '../utils/details';

const petPolicyCtaDef: SlotGroupDefinition = {
  label: 'Pet Policy Button',
  description: 'Hidden when the pet policy page is hidden.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Read the Pet Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/pet-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

interface Offer {
  enabled?: string | boolean;
}

const mtextV = 'm-0 text-sm leading-[22px] text-muted-foreground [&_p]:m-0';
const mbtn = 'btn btn-secondary self-start max-[767px]:self-stretch';

/**
 * Fees + PetPolicy summary (Paper 04): two columns on desktop, two rows of the phone "Before you apply"
 * list. Fees come from the application / admin fee fields and the fees & deposit sentence; pets from the pet fields.
 */
export function FeesAndPets() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const phone = useMediaQuery('(max-width: 767px)');
  const policy = useSlotGroup('feesandpets.pet_policy_cta', petPolicyCtaDef);

  const fees = feeFacts(p);
  const showFees = hasFees(fees) || editing;
  const pets = !!p.property_ispetfriendly;
  const offer = ((p.special_offers as Offer[]) || []).some((o) => String(o.enabled) === 'true');
  const restrictions = stripHtml(clean(p.property_pet_restrictions) || '');
  const showPolicy = !!pagePath('pet-policy');

  const feeCards: Array<[string, string, string]> = [
    fees.appFee ? ['Application fee', fees.appFee, 'per applicant'] : null,
    fees.adminFee ? ['Admin fee', fees.adminFee, 'one-time'] : null,
    fees.deposit ? ['Security deposit', fees.deposit[0], fees.deposit[1]] : null,
    fees.depositAlt ? ['Deposit alternative', 'Available', ''] : null,
  ].filter((c): c is [string, string, string] => !!c);
  const feeRows: Array<[string, string]> = [
    fees.appFee ? ['Application fee', `${fees.appFee} per applicant`] : null,
    fees.adminFee ? ['Administration fee', `${fees.adminFee} one-time`] : null,
    fees.deposit ? ['Security deposit', [fees.deposit[0], fees.deposit[1]].filter(Boolean).join(' ')] : null,
    fees.monthlyRange ? ['Required monthly charges', fees.monthlyRange] : null,
  ].filter((r): r is [string, string] => !!r);
  const feeSummary = [fees.appFee && `${fees.appFee} application`, fees.adminFee && `${fees.adminFee} admin`].filter(Boolean).join(' · ');

  const cells = (list: Array<[string, string, string]>) => (
    <div className="grid grid-cols-[1fr_1fr] gap-3 max-[767px]:gap-2">
      {list.map(([k, v, s]) => (
        <div className="flex flex-col gap-1.5 rounded-md border border-[--q-border] bg-[--q-surface] px-[18px] py-4 max-[767px]:p-3.5" key={k}>
          <span className="text-[11px] font-medium uppercase leading-[14px] tracking-[--q-tracking-label] text-muted-foreground">{k}</span>
          <span className="font-heading text-[26px] leading-[30px] max-[767px]:text-[22px] max-[767px]:leading-7">{v}</span>
          {s && <span className="text-[13px] leading-[18px] text-muted-foreground">{s}</span>}
        </div>
      ))}
    </div>
  );
  const policyBtn = (cls: string) => (
    <EditableSlotGroup groupPath="feesandpets.pet_policy_cta" definition={petPolicyCtaDef}>
      {showPolicy && pets && (
        <a className={cls} href={policy.url || PATHS.petPolicy} target={policy._target || '_self'}>
          {policy.label || 'Read the Pet Policy'} <span className="btn__arrow">→</span>
        </a>
      )}
    </EditableSlotGroup>
  );
  const petsBody = pets ? (
    <>
      {cells(petCells(p))}
      {restrictions && (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium leading-5">Restrictions</span>
          <p className={mtextV}>{restrictions}</p>
        </div>
      )}
      {policyBtn(mbtn)}
    </>
  ) : (
    <RichTextContent path="feesandpets.no_pets" as="p" className={mtextV} />
  );

  if (phone) {
    return (
      <section className="bg-[--q-paper] px-[--gutter]">
        {showFees && (
          <DetailsRow id="fees" title={<RichTextContent path="feesandpets.fees_row" as="span" />} summary={feeSummary}>
            {feeRows.length ? (
              <div className="flex flex-col">
                {feeRows.map(([k, v]) => (
                  <div className="flex justify-between gap-4 border-b border-b-[--q-border] px-0 py-2 text-sm leading-5 [&_b]:text-right [&_b]:font-medium [&_span]:text-muted-foreground" key={k}>
                    <span>{k}</span>
                    <b>{v}</b>
                  </div>
                ))}
              </div>
            ) : (
              fees.raw && <p className={mtextV}>{fees.raw}</p>
            )}
          </DetailsRow>
        )}
        <DetailsRow id="pets" title={<RichTextContent path="feesandpets.pets_row" as="span" />} summary={pets ? petSummary(p) : 'Not permitted'}>
          {petsBody}
        </DetailsRow>
      </section>
    );
  }

  if (!showFees && !pets) return null;

  return (
    <section
      className={`section grid gap-16 border-t border-t-[--q-border] max-[1199px]:grid-cols-[1fr] max-[1199px]:gap-14 ${
        pets && showFees ? 'grid-cols-[minmax(0,1fr)_minmax(0,1fr)]' : 'grid-cols-[minmax(0,760px)]'
      }`}
    >
      {showFees && (
        <div className="flex flex-col gap-5 [&_.h3_p]:m-0" id="fees" data-reveal>
          <RichTextContent path="feesandpets.fees_eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="feesandpets.fees_title" as="h2" className="h3" />
          {(offer || editing) && (
            <div className="flex flex-col gap-1 rounded-md bg-[--q-offer] px-4 py-3.5 text-sm leading-5 text-[--q-on-offer] [&_.textlink]:text-[--q-on-offer] [&_p]:m-0">
              <RichTextContent path="feesandpets.offer_note" as="p" />
              <button className="textlink self-start pt-2" type="button" onClick={() => window.dispatchEvent(new Event('site:open-offer'))}>
                <RichTextContent path="feesandpets.offer_link" as="span" /> <span className="btn__arrow">→</span>
              </button>
            </div>
          )}
          {feeCards.length > 0 && cells(feeCards)}
          {fees.monthlyText && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium leading-5">Required monthly charges</span>
              <p className={mtextV}>{fees.monthlyText}</p>
            </div>
          )}
          {fees.raw && <p className={mtextV}>{fees.raw}</p>}
          {!pets && <RichTextContent path="feesandpets.no_pets" as="p" className={`${mtextV} feespets__nopets`} />}
        </div>
      )}
      {pets && (
        <div className="flex flex-col gap-5 [&_.h3_p]:m-0" id="pets" data-reveal style={stagger(1)}>
          <RichTextContent path="feesandpets.pets_eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="feesandpets.pets_title" as="h2" className="h3" />
          {petsBody}
        </div>
      )}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeesAndPets',
  displayName: 'Fees and Pets',
  tokens: {
    'feesandpets.fees_eyebrow': { label: 'Fees · Eyebrow', type: 'richtext', defaultValue: '<p>Deposits and fees</p>' },
    'feesandpets.fees_title': { label: 'Fees · Title', type: 'richtext', defaultValue: '<p>What it costs to move in.</p>' },
    'feesandpets.offer_note': {
      label: 'Fees · Offer Notice',
      description: 'Shown only while a special offer is enabled.',
      type: 'richtext',
      defaultValue: '<p>A current rent special lowers some of these costs.</p>',
    },
    'feesandpets.offer_link': { label: 'Fees · Offer Link Text', type: 'richtext', defaultValue: '<p>See the Offer</p>' },
    'feesandpets.pets_eyebrow': { label: 'Pets · Eyebrow', type: 'richtext', defaultValue: '<p>Pets</p>' },
    'feesandpets.pets_title': { label: 'Pets · Title', type: 'richtext', defaultValue: '<p>Bring the whole household.</p>' },
    'feesandpets.no_pets': {
      label: 'No Pets Line',
      description: 'Shown instead of the pet cards when the property is not pet friendly.',
      type: 'richtext',
      defaultValue: '<p>Pets are not permitted; assistance animals are not pets.</p>',
    },
    'feesandpets.fees_row': { label: 'Fees Row Title (phone)', type: 'richtext', defaultValue: '<p>Deposits and fees</p>' },
    'feesandpets.pets_row': { label: 'Pets Row Title (phone)', type: 'richtext', defaultValue: '<p>Pets</p>' },
  },
  slotGroups: {
    'feesandpets.pet_policy_cta': petPolicyCtaDef,
  },
};

(FeesAndPets as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FeesAndPets;
